/**
 * ConditionValidator.js (Improved version with robust error handling)
 * Async condition validator for discount rules with defensive programming
 */

export default class ConditionValidator {
    constructor(logger = null) {
      this.logger = logger || console;
    }
  
    /**
     * Validate a discount against cart state
     * @param {Object} discount - Discount object to validate
     * @param {Object} cartState - Current cart state
     * @returns {Object} Validation result with valid flag and reason
     */
    async validateDiscount(discount, cartState) {
      try {
        // Input validation
        if (!discount || typeof discount !== 'object') {
          return { valid: false, reason: 'Invalid discount object' };
        }
  
        if (!cartState || typeof cartState !== 'object') {
          return { valid: false, reason: 'Invalid cart state' };
        }
  
        // Date validation
        if (!await this.isDiscountDateValid(discount)) {
          return { valid: false, reason: 'Date range invalid' };
        }
  
        // Condition validation
        if (!await this.validateDiscountConditions(discount, cartState)) {
          return { valid: false, reason: 'Conditions not met' };
        }
  
        // Additional async validations can be added here
        return { valid: true };
      } catch (error) {
        this.logger.error('Error validating discount:', error);
        return { valid: false, reason: 'Validation error' };
      }
    }
  
    /**
     * Check if discount date range is valid
     * @param {Object} discount - Discount object
     * @returns {boolean} True if date is valid or no date restrictions
     */
    async isDiscountDateValid(discount) {
      if (!discount || !discount.date) return true;
  
      try {
        const now = new Date();
        const startDate = discount.date.start ? new Date(discount.date.start) : null;
        const endDate = discount.date.end ? new Date(discount.date.end) : null;
  
        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
  
        return true;
      } catch (error) {
        this.logger.error('Error validating discount date:', error);
        return true; // Default to valid if date parsing fails
      }
    }
  
    /**
     * Validate all discount conditions
     * @param {Object} discount - Discount object
     * @param {Object} cartState - Cart state
     * @returns {boolean} True if conditions are met
     */
    async validateDiscountConditions(discount, cartState) {
      try {
        if (!discount || !cartState) {
          return false;
        }
  
        if (!discount.conditions || Object.keys(discount.conditions).length === 0) {
          return true; // No conditions means always valid
        }
  
        const conditionResults = await Promise.all(
          Object.entries(discount.conditions).map(([condId, condition]) =>
            this.checkCondition(condition, cartState)
          )
        );
  
        if (discount.requirement === 'all') {
          return conditionResults.every(result => result);
        } else {
          return conditionResults.some(result => result);
        }
      } catch (error) {
        this.logger.error('Error validating discount conditions:', error);
        return false;
      }
    }
  
    /**
     * Check a single condition against cart state
     * @param {Object} condition - Condition to check
     * @param {Object} cartState - Cart state
     * @returns {boolean} True if condition is met
     */
    async checkCondition(condition, cartState) {
      try {
        // Input validation
        if (!condition || typeof condition !== 'object') {
          return false;
        }
  
        if (!cartState || typeof cartState !== 'object') {
          return false;
        }
  
        const categorySummary = cartState.categorySummary;
        if (!categorySummary || typeof categorySummary !== 'object') {
          return false;
        }
  
        // Safe service name access
        const serviceName = condition.service;
        if (!serviceName || typeof serviceName !== 'string') {
          return false;
        }
  
        let categoryData = categorySummary[serviceName];
  
        // If not found by service name, try by service_id
        if (!categoryData && condition.service_id) {
          categoryData = Object.values(categorySummary).find(category =>
              category.items && category.items.some(item =>
                String(item.category_id) === String(condition.service_id)
              )
          );
        }
  
        if (!categoryData) {
          // Check addons if service_id provided
          if (condition.service_id) {
            const addonQuantity = await this.getAddonQuantityForService(
              condition.service_id,
              cartState
            );
            if (addonQuantity > 0) {
              const requiredQuantity = parseInt(condition.qty, 10) || 0;
              return this.checkQuantityCondition(addonQuantity, requiredQuantity, condition.condition);
            }
          }
          return false;
        }
  
        const actualQuantity = categoryData.quantity || 0;
        const requiredQuantity = parseInt(condition.qty, 10) || 0;
        return this.checkQuantityCondition(actualQuantity, requiredQuantity, condition.condition);
  
      } catch (error) {
        this.logger.error('Error checking condition:', error);
        return false;
      }
    }
  
    /**
     * Check quantity condition based on condition type
     * @param {number} actual - Actual quantity
     * @param {number} required - Required quantity
     * @param {string} conditionType - Type of condition (at_least, equal_to, less_than)
     * @returns {boolean} True if condition is satisfied
     */
    checkQuantityCondition(actual, required, conditionType) {
      try {
        switch (conditionType) {
          case 'less_than':
            return actual < required;
          case 'equal_to':
            return actual >= required;
          case 'at_least':
          default:
            return actual >= required;
        }
      } catch (error) {
        this.logger.error('Error checking quantity condition:', error);
        return false;
      }
    }
  
    /**
     * Get addon quantity for a specific service
     * @param {string} serviceId - Service ID to match
     * @param {Object} cartState - Cart state
     * @returns {number} Total addon quantity for the service
     */
    async getAddonQuantityForService(serviceId, cartState) {
      try {
        if (!cartState || !cartState.items || !Array.isArray(cartState.items)) {
          return 0;
        }
  
        let totalQuantity = 0;
  
        for (const item of cartState.items) {
          if (item && item.addons && Array.isArray(item.addons)) {
            for (const addon of item.addons) {
              // Check if addon matches the service_id
              if (this.addonMatchesService(addon, serviceId)) {
                totalQuantity += addon.quantity || 0;
              }
            }
          }
        }
  
        return totalQuantity;
      } catch (error) {
        this.logger.error('Error getting addon quantity:', error);
        return 0;
      }
    }
  
    /**
     * Check if addon matches a service ID
     * @param {Object} addon - Addon object
     * @param {string} serviceId - Service ID to match
     * @returns {boolean} True if addon matches service
     */
    addonMatchesService(addon, serviceId) {
      try {
        if (!addon || typeof addon !== 'object') {
          return false;
        }
  
        // Map addon categories to service IDs
        const serviceMapping = {
          "Dryer Vents": "262",
          "Dryer Vent Cleaning": "262",
          // Add other mappings as needed
        };
  
        const addonServiceId = serviceMapping[addon.category] || serviceMapping[addon.addon_name];
        return String(addonServiceId) === String(serviceId);
      } catch (error) {
        this.logger.error('Error matching addon service:', error);
        return false;
      }
    }
  
    /**
     * Validate condition object structure
     * @param {Object} condition - Condition to validate
     * @returns {Object} Validation result
     */
    validateConditionObject(condition) {
      const result = {
        valid: true,
        issues: []
      };
  
      try {
        if (!condition || typeof condition !== 'object') {
          result.valid = false;
          result.issues.push('Condition is not a valid object');
          return result;
        }
  
        if (!condition.service && !condition.service_id) {
          result.issues.push('Missing service or service_id');
        }
  
        if (!condition.condition) {
          result.issues.push('Missing condition type');
        }
  
        if (condition.qty === undefined || condition.qty === null) {
          result.issues.push('Missing quantity requirement');
        }
  
        const validConditions = ['at_least', 'equal_to', 'less_than'];
        if (condition.condition && !validConditions.includes(condition.condition)) {
          result.issues.push(`Invalid condition type: ${condition.condition}`);
        }
  
        if (condition.qty !== undefined && isNaN(parseInt(condition.qty, 10))) {
          result.issues.push('Quantity is not a valid number');
        }
  
        result.valid = result.issues.length === 0;
      } catch (error) {
        result.valid = false;
        result.issues.push(`Validation error: ${error.message}`);
      }
  
      return result;
    }
  
    /**
     * Normalize condition object
     * @param {Object} condition - Raw condition object
     * @returns {Object|null} Normalized condition or null if invalid
     */
    normalizeCondition(condition) {
      try {
        if (!condition || typeof condition !== 'object') {
          return null;
        }
  
        const normalized = {
          service: condition.service || null,
          service_id: condition.service_id || null,
          condition: condition.condition || 'at_least',
          qty: String(parseInt(condition.qty, 10) || 0)
        };
  
        // Validate the normalized condition
        const validation = this.validateConditionObject(normalized);
        if (!validation.valid) {
          this.logger.warn(`Condition normalization failed:`, validation.issues);
          return null;
        }
  
        return normalized;
      } catch (error) {
        this.logger.error('Error normalizing condition:', error);
        return null;
      }
    }
  
    /**
     * Get summary of cart state for debugging
     * @param {Object} cartState - Cart state
     * @returns {Object} Summary object
     */
    getCartStateSummary(cartState) {
      try {
        if (!cartState || typeof cartState !== 'object') {
          return { valid: false, reason: 'Invalid cart state' };
        }
  
        return {
          valid: true,
          itemCount: cartState.items ? cartState.items.length : 0,
          categoryCount: cartState.categorySummary ? Object.keys(cartState.categorySummary).length : 0,
          categories: cartState.categorySummary ? Object.keys(cartState.categorySummary) : [],
          totalQuantity: cartState.categorySummary ?
            Object.values(cartState.categorySummary).reduce((sum, cat) => sum + (cat.quantity || 0), 0) : 0
        };
      } catch (error) {
        this.logger.error('Error getting cart summary:', error);
        return { valid: false, reason: 'Error processing cart state' };
      }
    }
}
